import { Button, IconButton, Typography } from "@povio/ui";
import { useQueryClient } from "@tanstack/react-query";
import type { PropsWithChildren } from "react";
import { useEffect, useMemo, useState } from "react";
import { Group, Panel, Separator, usePanelRef } from "react-resizable-panels";

import { ArrowRightIcon } from "@/assets/icons/general/ArrowRight";
import { ChevronLeftIcon } from "@/assets/icons/general/ChevronLeft";
import { PanelCloseIcon } from "@/assets/icons/navbar/PanelCloseIcon";
import { DesktopHeader } from "@/components/shared/layout/navbar/header/DesktopHeader";
import { DesktopNavbar } from "@/components/shared/layout/sidebar/DesktopNavbar";
import { StocksTable } from "@/components/stocks/StocksTable";
import { JobDetails } from "@/components/watchlist/components/JobDetails";
import { useJobSalaries } from "@/components/watchlist/hooks/useJobSalaries";
import { useRelatedJobSalaries } from "@/components/watchlist/hooks/useRelatedJobSalaries";
import { WatchlistTable } from "@/components/watchlist/WatchlistTable";
import { CommonModels } from "@/data/common/common.models";
import type { WatchlistModels } from "@/data/watchlist/watchlist.models";
import { WatchlistQueries } from "@/data/watchlist/watchlist.queries";
import { useToast } from "@/hooks/useToast";
import { DemoContext } from "@/providers/demo/DemoContext";

import LiveshowGradient from "./banners/LiveshowGradient";

const jobFitProcessingStatuses: string[] = [CommonModels.StatusEnum.enqueued, CommonModels.StatusEnum.processing];

export const PageLayoutDesktop = ({ children }: PropsWithChildren) => {
  const { errorToast } = useToast();
  const queryClient = useQueryClient();
  const watchlistPanelRef = usePanelRef();
  const [isWatchlistCollapsed, setIsWatchlistCollapsed] = useState(false);
  const [selectedTab, setSelectedTab] = useState<"watchlist" | "stocks">("watchlist");
  const [viewMode, setViewMode] = useState<"watchlist" | "jobDetails">("watchlist");
  const [jobFitData, setJobFitData] = useState<WatchlistModels.WatchlistJobFitDto | null>(null);

  // In demo mode, use mock data
  const { isDemoMode, mockAuth } = DemoContext.useDemo();

  const {
    quantiles,
    selectedMetroArea,
    selectedWatchlistGroup,
    selectedWatchlist,
    selectedJobSalary,
    setSelectedJobSalary,
    selectedQuantile,
    setSelectedQuantile,
  } = useJobSalaries({
    defaultToAll: true,
    autoSelectDefaults: true,
  });

  const { relatedJobSalaries } = useRelatedJobSalaries(selectedJobSalary);

  const { data: jobFits } = WatchlistQueries.useGetJobFits();

  const preloadedJobFit = useMemo(() => {
    if (!selectedJobSalary?.title || !jobFits?.items) return null;
    const item = jobFits.items.find((i) => i.jobTitle === selectedJobSalary.title);
    if (!item || item.status !== CommonModels.StatusEnum.completed) return null;
    return { fitScore: item.fitScore, status: item.status };
  }, [selectedJobSalary?.title, jobFits?.items]);

  const { mutate: calculateJobFit } = WatchlistQueries.useCalculateJobFit({
    onSuccess: (response) => {
      setJobFitData(response.data ?? null);
      queryClient.invalidateQueries({ queryKey: WatchlistQueries.keys.getJobFits() });
    },
    onError: (error) => {
      if (error instanceof Error && error.message.toLowerCase().includes("quota exceeded")) {
        errorToast({
          text: error.message,
        });
      }
    },
  });

  const onCalculateJobFit = () => {
    calculateJobFit({
      data: {
        jobTitle: selectedJobSalary?.title || "",
        industry: selectedWatchlistGroup?.title || null,
        subIndustry: selectedWatchlist?.title || null,
      },
    });
  };

  const { mutate: findJobFitByTitle, isPending: isLoadingJobFit } = WatchlistQueries.useFindJobFitByTitle({
    onSuccess: (response) => {
      setJobFitData(response.data ?? null);
    },
    onError: () => {},
  });

  // Fetch job fit data immediately when job title changes
  useEffect(() => {
    if (!selectedJobSalary?.title) {
      setJobFitData(null);
      return;
    }

    findJobFitByTitle({
      data: {
        jobTitle: selectedJobSalary.title,
      },
    });
  }, [selectedJobSalary?.title, findJobFitByTitle]);

  useEffect(() => {
    // only poll if the job fit data is processing or enqueued
    const shouldPoll = jobFitData?.status && jobFitProcessingStatuses.includes(jobFitData.status);
    if (!shouldPoll || !selectedJobSalary?.title) {
      return;
    }

    const interval = setInterval(() => {
      findJobFitByTitle({
        data: {
          jobTitle: selectedJobSalary.title,
        },
      });
    }, 5000);

    return () => clearInterval(interval);
  }, [jobFitData?.status, selectedJobSalary?.title, findJobFitByTitle]);

  const handleWatchlistResize = (size: { asPercentage: number; inPixels: number }) => {
    // If the watchlist is less than 20% of the screen and not 0 (collapsed), set it to 20%
    if (size.asPercentage < 20 && size.asPercentage !== 0) {
      watchlistPanelRef.current?.resize("20%");
    }
    setIsWatchlistCollapsed(size.asPercentage === 0);
  };
  const handleExpandWatchlist = () => {
    watchlistPanelRef.current?.expand();
    setIsWatchlistCollapsed(false);
  };

  const handleJobSalarySelected = () => {
    setViewMode("jobDetails");
  };

  const handleBackToWatchlist = () => {
    setViewMode("watchlist");
  };

  return (
    <div className="flex h-full w-full flex-col overflow-hidden">
      <DesktopHeader />
      <Group
        className="*:data-panel:transition-[flex-basis] *:data-panel:duration-300 *:data-panel:ease-out"
        orientation="horizontal"
      >
        <Panel
          id="watchlist"
          className="min-w-0! transition-[flex-basis] duration-300 ease-out"
          collapsible
          defaultSize="30%"
          maxSize="50%"
          minSize="0%"
          collapsedSize="0%"
          onResize={handleWatchlistResize}
          panelRef={watchlistPanelRef}
        >
          <div className="@container/watchlist h-[calc(100dvh-70px)]">
            <div className="flex flex-row items-center justify-between px-2 py-1-5">
              {viewMode === "watchlist" ? (
                <>
                  <div className="flex flex-row items-center gap-2">
                    {/* TODO:Replace btn with Text button or Toggle button */}
                    <button
                      type="button"
                      className="cursor-pointer outline-hidden"
                      onClick={() => setSelectedTab("watchlist")}
                      aria-label="Switch to Jobs"
                    >
                      <Typography
                        size="label-1"
                        variant="prominent-1"
                        className={selectedTab === "watchlist" ? "text-text-default-1" : "text-text-default-3"}
                      >
                        Jobs
                      </Typography>
                    </button>
                    <button
                      type="button"
                      className="cursor-pointer outline-hidden"
                      onClick={() => setSelectedTab("stocks")}
                      aria-label="Switch to Markets"
                    >
                      <Typography
                        size="label-1"
                        variant="prominent-1"
                        className={selectedTab === "stocks" ? "text-text-default-1" : "text-text-default-3"}
                      >
                        Markets
                      </Typography>
                    </button>
                  </div>
                  <IconButton
                    className="p-0!"
                    label="Panel close"
                    disableTooltip
                    size="s"
                    variant="text"
                    color="secondary"
                    icon={PanelCloseIcon}
                    onPress={() => watchlistPanelRef.current?.collapse()}
                  />
                </>
              ) : (
                <>
                  <Button
                    variant="outlined"
                    color="secondary"
                    size="xs"
                    className="h-[30px]"
                    icon={ChevronLeftIcon}
                    onPress={handleBackToWatchlist}
                  >
                    Back
                  </Button>
                  <IconButton
                    className="p-0!"
                    label="Panel close"
                    disableTooltip
                    size="s"
                    variant="text"
                    color="secondary"
                    icon={PanelCloseIcon}
                    onPress={() => watchlistPanelRef.current?.collapse()}
                  />
                </>
              )}
            </div>
            {viewMode === "watchlist" ? (
              selectedTab === "watchlist" ? (
                <WatchlistTable
                  jobSalaryChanged={handleJobSalarySelected}
                  isDesktop
                  jobFits={jobFits ?? null}
                />
              ) : (
                <StocksTable />
              )
            ) : (
              <div className="no-scrollbar h-full overflow-y-auto">
                <JobDetails
                  selectedJobSalary={selectedJobSalary}
                  selectedMetroArea={selectedMetroArea}
                  quantiles={quantiles}
                  selectedQuantile={selectedQuantile}
                  setSelectedQuantile={setSelectedQuantile}
                  jobFitData={isDemoMode ? (mockAuth.mockJobFitData ?? null) : jobFitData}
                  preloadedJobFit={isDemoMode ? undefined : preloadedJobFit}
                  onCalculateJobFit={onCalculateJobFit}
                  isLoadingJobFit={isDemoMode ? false : isLoadingJobFit}
                  relatedJobSalaries={relatedJobSalaries || []}
                  setSelectedJobSalary={(jobSalary) => {
                    setSelectedJobSalary(jobSalary);
                    const scrollContainer = document.getElementById("scroll-container");
                    scrollContainer?.scrollTo({ top: 300, behavior: "smooth" });
                  }}
                  isDemoModeOrShowDemoData={isDemoMode}
                />
              </div>
            )}
          </div>
        </Panel>
        <Separator
          style={{ borderLeft: "10px solid transparent", borderRight: "10px solid transparent", margin: "0 -10px" }}
        >
          <div className="flex h-[calc(100dvh-70px)] w-3 flex-row items-center justify-center gap-0-5 border-elevation-outline-default-1 border-x bg-elevation-fill-default-1">
            {isWatchlistCollapsed ? (
              <button
                type="button"
                className="flex h-full items-center justify-center rounded-sm outline-offset-2 focus-visible:outline-2 focus-visible:outline-elevation-outline-default-1"
                aria-label="Show Watchlist"
                onClick={handleExpandWatchlist}
              >
                <ArrowRightIcon className="size-5 text-text-default-3" />
              </button>
            ) : (
              <>
                <div className="h-5 w-px rounded-6xl bg-text-default-3" />
                <div className="h-5 w-px rounded-6xl bg-text-default-3" />
              </>
            )}
          </div>
        </Separator>
        <Panel
          defaultSize="70%"
          minSize="50%"
          maxSize="100%"
        >
          <div className="relative flex w-full flex-row">
            <DesktopNavbar />
            <main className="@container/main relative z-10 flex h-[calc(100dvh-70px)] w-full flex-col overflow-y-auto bg-elevation-fill-default-0">
              <LiveshowGradient />
              {children}
            </main>
          </div>
        </Panel>
        <Separator />
      </Group>
    </div>
  );
};
